// ResetPassword component
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  Link,
} from '@mui/material';
import { LockResetOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('auth.resetPassword.noToken', 'No reset token provided'));
    }
  }, [token, t]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    // Validation
    if (!formData.newPassword) {
      setError(t('auth.resetPassword.passwordRequired', 'Password is required'));
      return;
    }

    if (formData.newPassword.length < 6) {
      setError(t('auth.resetPassword.passwordTooShort', 'Password must be at least 6 characters'));
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('auth.resetPassword.passwordMismatch', 'Passwords do not match'));
      return;
    }

    if (!token) {
      setError(t('auth.resetPassword.noToken', 'No reset token provided'));
      return;
    }

    setLoading(true);
    try {
      await apiService.post('/api/auth/reset-password', {
        token,
        new_password: formData.newPassword,
      });

      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          replace: true,
          state: { message: t('auth.resetPassword.successRedirect', 'Password reset successfully. Please log in.') }
        });
      }, 3000);
    } catch (err: any) {
      console.error('Failed to reset password:', err);
      const errorMessage = err.response?.data?.detail || err.detail || err.message || 
        t('auth.resetPassword.error', 'Failed to reset password. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ m: 1, bgcolor: 'error.main' }}>
                  <LockResetOutlined />
                </Avatar>
                <Typography component="h1" variant="h5">
                  {t('auth.resetPassword.invalid', 'Invalid Reset Link')}
                </Typography>
              </Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                {t('auth.resetPassword.noToken', 'No reset token provided')}
              </Alert>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{ mt: 2 }}
              >
                {t('auth.backToLogin', 'Back to Login')}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ m: 1, bgcolor: 'success.main' }}>
                  <LockResetOutlined />
                </Avatar>
                <Typography component="h1" variant="h5">
                  {t('auth.resetPassword.success', 'Password Reset Successful!')}
                </Typography>
              </Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t(
                  'auth.resetPassword.successMessage',
                  'Your password has been reset successfully. Redirecting to login...'
                )}
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                <LockResetOutlined />
              </Avatar>
              <Typography component="h1" variant="h5">
                {t('auth.resetPassword.title', 'Reset Password')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                {t('auth.resetPassword.subtitle', 'Enter your new password')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label={t('auth.resetPassword.newPassword', 'New Password')}
                type="password"
                id="newPassword"
                autoComplete="new-password"
                value={formData.newPassword}
                onChange={handleInputChange}
                disabled={loading}
                helperText={t('auth.resetPassword.passwordHint', 'Minimum 6 characters')}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t('auth.resetPassword.confirmPassword', 'Confirm Password')}
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                disabled={loading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !formData.newPassword || !formData.confirmPassword}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  t('auth.resetPassword.reset', 'Reset Password')
                )}
              </Button>
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ cursor: 'pointer' }}
                >
                  {t('auth.backToLogin', 'Back to Login')}
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ResetPassword;






